<!-- src/views/ZoneForm.vue -->
<template>
  <v-container fluid>
    <!-- CREATION MODE: Step 1 - Select Zone and Form -->
    <v-row v-if="!isEdit && !id && !formJson">
      <v-col cols="12">
        <v-card class="finding-shell-card">
          <v-card-title>
            <div class="d-flex align-center">
              <v-btn icon small class="mr-4" @click="$router.back()">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <div>
                <div class="finding-page-title">Create Finding</div>
                <div class="finding-page-subtitle">Submit a new finding from desktop</div>
              </div>
            </div>
          </v-card-title>
          <v-card-text>
            <v-form ref="creationForm">
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newFinding.zoneId"
                    :items="zones"
                    item-title="name"
                    item-value="zoneId"
                    label="Zone *"
                    :rules="[r.required]"
                    required
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-map-marker"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newFinding.formId"
                    :items="formTemplates"
                    item-title="name"
                    item-value="formId"
                    label="Form Template *"
                    :rules="[r.required]"
                    required
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-form-select"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newFinding.inspectionId"
                    :items="inspections"
                    item-title="name"
                    item-value="inspectionId"
                    label="Inspection (Optional)"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-clipboard-text"
                    clearable
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="$router.back()">Cancel</v-btn>
            <v-btn color="primary" @click="onCreationStep1Next">Next</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- CREATION MODE: Step 2 - Fill Form and Select Location -->
    <v-row v-if="!isEdit && !id && formJson && !finding">
      <v-col cols="12">
        <v-card-title class="d-flex justify-space-between align-center flex-nowrap finding-page-header">
          <div class="d-flex align-center">
            <v-btn icon small class="mr-4" @click="onCreationBack">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <div>
              <div class="finding-page-title">Create Finding</div>
              <div class="finding-page-subtitle">Fill form and select location</div>
            </div>
          </div>
        </v-card-title>
      </v-col>
    </v-row>
    <v-row v-if="!isEdit && !id && formJson && !finding" class="fill-height" no-gutters>
      <v-col cols="12" lg="7" class="pr-lg-3">
        <v-card elevation="1" variant="outlined" class="finding-panel-card mb-3">
          <v-card-title>
            <v-icon class="mr-2" color="primary">mdi-information-outline</v-icon>
            <span>Finding Details</span>
          </v-card-title>
          <v-card-text>
            <v-alert
              v-if="!findingStatuses.length || !priorities.length"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <div class="text-body-2">
                Status / Priority lookups are empty
                ({{ findingStatuses.length }} statuses, {{ priorities.length }} priorities loaded).
                Open DevTools console for the [loadLookups] log, or define them in
                <strong>Utilities &rarr; Lookup Types &rarr; FindingStatus / FindingPriority</strong>.
              </div>
            </v-alert>
            <v-checkbox v-model="newFinding.isSafetyIssue" label="Safety issue" color="error" hide-details />
            <v-row dense>
              <v-col cols="12" md="4"><v-text-field v-model="newFinding.submitTime" type="datetime-local" label="Date raised" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="newFinding.dateMadeSafe" type="datetime-local" label="Date made safe" clearable /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="newFinding.dateResolved" type="datetime-local" label="Date resolved" clearable /></v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="newFinding.statusId"
                  :items="findingStatuses"
                  item-title="findingStatus"
                  :item-value="(s) => Number(s.findingStatusId)"
                  :label="`Status (${findingStatuses.length})`"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-flag"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="newFinding.priorityId"
                  :items="priorities"
                  item-title="priority"
                  :item-value="(p) => Number(p.priorityId)"
                  :label="`Priority (${priorities.length})`"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-alert"
                  clearable
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="newFinding.details"
                  label="Description (Optional)"
                  placeholder="Describe what was found. Leave blank to default to 'Finding from Desktop'."
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-text-box"
                  rows="3"
                  auto-grow
                />
              </v-col>
              <v-col cols="12">
                <div class="fs-section-heading mb-2 d-flex align-center justify-space-between">
                  <span>Media</span>
                  <v-btn
                    color="primary"
                    variant="tonal"
                    size="small"
                    prepend-icon="mdi-camera-plus"
                    @click="$refs.creationPhotoInput.click()"
                  >
                    Add Photos
                  </v-btn>
                  <input
                    ref="creationPhotoInput"
                    type="file"
                    accept="image/*"
                    multiple
                    style="display: none"
                    @change="onCreationPhotoInputChange"
                  />
                </div>
                <v-row v-if="creationPhotoPreviews.length" dense class="mt-1">
                  <v-col
                    v-for="(preview, idx) in creationPhotoPreviews"
                    :key="preview.url"
                    cols="auto"
                  >
                    <v-sheet
                      elevation="1"
                      rounded
                      class="pa-0 position-relative"
                      style="overflow: hidden; width: 80px; height: 80px"
                    >
                      <v-img
                        :src="preview.url"
                        height="80"
                        width="80"
                        class="rounded"
                        cover
                      />
                      <v-btn
                        icon
                        size="x-small"
                        color="error"
                        variant="flat"
                        class="position-absolute"
                        style="top: 2px; right: 2px; z-index: 10"
                        @click.stop="removeCreationPhoto(idx)"
                      >
                        <v-icon size="16">mdi-close</v-icon>
                      </v-btn>
                    </v-sheet>
                    <div class="text-caption text-grey text-truncate mt-1" style="max-width: 80px" :title="preview.name">
                      {{ formatFileSize(preview.size) }}
                    </div>
                  </v-col>
                </v-row>
                <div v-else class="text-center text-grey py-3 text-caption">
                  No images selected. Click <strong>Add Photos</strong> to attach images to this finding.
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card elevation="1" variant="outlined" class="finding-panel-card">
          <v-card-title>
            <v-icon class="mr-2" color="primary">mdi-map</v-icon>
            <span>Select Location</span>
          </v-card-title>
          <v-card-text>
            <ZoneMapSimple
              v-if="selectedZoneId"
              :zone-id="selectedZoneId"
              height="400px"
              :allow-location-selection="true"
              @location-selected="onLocationSelected"
            />
            <div v-if="newFinding.latitude && newFinding.longitude" class="mt-2">
              <v-chip color="success" variant="tonal">
                <v-icon start>mdi-map-marker</v-icon>
                Location: {{ newFinding.latitude.toFixed(6) }}, {{ newFinding.longitude.toFixed(6) }}
              </v-chip>
            </div>
            <v-alert v-else type="info" variant="tonal" class="mt-2">
              Please click on the map to select a location for this finding.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="5">
        <v-card class="sticky-card finding-panel-card" elevation="1" variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-form-select</v-icon>
            <span class="text-h6">Form Details</span>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <FormViewer
              ref="formViewer"
              v-if="formJson"
              :formJson="formJson"
              submitLabel="Save Finding"
              :showSubmit="true"
              @submit="handleCreationFormSubmit"
            />
            <div v-else class="text-center text-grey py-8">
              <v-progress-circular indeterminate color="primary" />
              <div class="text-body-1 mt-2">Loading form...</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- HEADER ROW (Edit Mode) -->
    <v-row v-if="finding">
      <v-col cols="12">
        <v-card-title class="d-flex justify-space-between align-center flex-nowrap finding-page-header">
          <div class="d-flex align-center">
            <v-btn icon small class="mr-4" @click="$router.back()">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <div>
              <div class="finding-page-title d-flex align-center">
                {{ finding?.title || "Finding" }}
                <audit-field-timeline
                  v-if="finding && finding.findingId"
                  entity-type="Finding"
                  :record-id="finding.findingId"
                  field-key="Title"
                  field-label="Title"
                />
              </div>
              <div class="finding-page-subtitle">Add or edit a finding</div>
            </div>
          </div>

          <!-- Hidden when opened from the map view (?from=map) -->
          <div v-if="!openedFromMap" class="d-flex align-center" style="gap: 8px; white-space: nowrap">
            <audit-history-button
              v-if="finding && finding.findingId"
              entity-type="Finding"
              :record-id="finding.findingId"
              title="Finding Change History"
            />
            <v-btn
              color="green"
              dark
              small
              flat
              :loading="savingAll"
              @click="onSaveAll"
            >
              <v-icon left size="18">mdi-content-save</v-icon>
              Save
            </v-btn>
            <v-btn color="success" dark small flat @click="showCreateTaskDialog = true">
              <v-icon left size="18">mdi-clipboard-plus</v-icon>
              Create Task
            </v-btn>
            <v-btn color="primary" dark small flat @click="onPrintFinding">
              <v-icon left size="18">mdi-file-pdf-box</v-icon>
              Export PDF
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <!-- Body ROW-->
    <v-row v-if="finding" class="fill-height" no-gutters>
      <!-- LEFT COLUMN: Finding Details -->
      <v-col cols="12" lg="7" class="pr-lg-3">
        <!-- Finding Summary Card -->
        <FindingSummaryCard
          ref="summary"
          v-if="finding"
          :finding="finding"
          :form="formRecord"
          :media="media"
          :status-label="'Action Needed'"
          :status-color="'red-lighten-4'"
          :status-text-color="'red-darken-2'"
          :show-map="true"
          :map-zoom="12"
          :statuses="findingStatuses"
          :priorities="priorities"
          :inspections="inspections"
          :external-save="true"
          @save="onSaveFinding"
          @media-updated="onMediaUpdated"
        />
      </v-col>

      <!-- RIGHT COLUMN: Form Details -->
      <v-col cols="12" lg="5">
        <v-card class="sticky-card finding-panel-card" elevation="1" variant="outlined">
          <v-card-title class="d-flex align-center finding-form-titlebar">
            <v-icon class="mr-2" color="primary">mdi-form-select</v-icon>
            <span class="text-h6 font-weight-bold">Form Details</span>
            <v-spacer />
            <v-chip v-if="finding?.title" size="small" color="primary" variant="tonal">
              {{ finding.title }}
            </v-chip>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4 finding-form-shell">
            <FormViewer
              ref="formViewer"
              v-if="formJson"
              :formJson="formJson"
              title=""
              submitLabel="Save"
              :showSubmit="false"
              @submit="handleFormSubmit"
            />
            <div v-else class="text-center text-grey py-8">
              <v-icon size="48" class="mb-2">mdi-form-select</v-icon>
              <div class="text-body-1">No form data available</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create Task Dialog -->
    <v-dialog v-model="showCreateTaskDialog" max-width="800px" persistent scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-clipboard-plus</v-icon>
          <span class="text-h6">Create Task</span>
          <v-spacer />
          <v-btn icon variant="text" @click="showCreateTaskDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <TaskForm
            v-if="finding"
            v-model="task"
            :finding="finding"
            :users="users"
            :isEdit="false"
            @saved="onTaskSaved"
            @cancel="showCreateTaskDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { convertCoords } from "@/utils/coordinateUtils";
import { findingDateInput, findingDatePayload } from "@/utils/findingDates";

import TaskForm from "@/components/TaskForm.vue";
import ZoneMapSimple from "@/components/ZoneMapSimple.vue";
import FormViewer from "@/components/FormViewer.vue";
import FindingSummaryCard from "@/components/FindingSummaryDetailPanel.vue";
import AuditFieldTimeline from "@/components/audit/AuditFieldTimeline.vue";
import { icons, iconsNums } from "@/assets/icons";
import { useAuthStore } from "@/stores/auth";

import { markRaw } from "vue";
import { useToast } from "vue-toastification";

import { generateFindingReport } from "@/api/reports";
import apiClient from "@/api/client";
import { fetchUsers, findingStatus as fetchFindingStatusDropdown, findingPriority as fetchFindingPriorityDropdown } from "@/api/dropdowns";
import { createTask } from "@/api/tasks";
import { fetchForm, fetchFormsAll } from "@/api/formsTemplates";
import { getPhotosByFindingId, uploadPhoto, snapshotPhotoFiles, revokePhotoSnapshot, revokePhotoSnapshots } from "@/api/photoItems";
import { getById, submitFormResponse, updateFinding, createFinding } from "@/api/findings";
import { fetchZones } from "@/api/zones";
import { getAll as getAllInspections } from "@/api/inspections";
import { useDialogStore } from "@/stores/dialog";

export default {
  name: "Finding-",
  setup() {
    const toast = useToast();
    const dialog = useDialogStore();
    return { toast, dialog };
  },
  data() {
    return {
      _finding: null,
      findingVersion: 0,
      loading: false,
      search: "",
      users: [],
      formJson: null,
      // Raw form template record for the loaded finding's formId. Passed to
      // FindingSummaryCard so its map marker can fall back to the form's
      // iconName when the finding row itself doesn't carry one.
      formRecord: null,
      submitting: false, // while POSTing the response
      submitStatus: "", // success or error message
      submittingLock: false,
      savingAll: false, // single header Save (finding fields + form) in flight
      media: [],
      lightbox: {
        show: false,
        img: null,
      },
      task: {
        title: "",
        description: "",
        zoneId: null,
        findingId: null,
        assignedTo: null,
        dueDate: null,
        estimatedCost: null,
        priority: null,
        latitude: null,
        longitude: null,
      },
      findingStatuses: [],
      priorities: [],
      showCreateTaskDialog: false,
      // Creation mode data
      zones: [],
      formTemplates: [],
      inspections: [],
      newFinding: {
        isSafetyIssue: false,
        submitTime: findingDateInput(new Date().toISOString()),
        dateMadeSafe: '',
        dateResolved: '',
        zoneId: null,
        formId: null,
        inspectionId: null,
        title: "",
        details: "",
        statusId: null,
        priorityId: null,
        latitude: null,
        longitude: null,
      },
      creationPhotos: [],
      r: {
        required: v => !!v || "Required",
      },
    };
  },
  components: { ZoneMapSimple, FormViewer, FindingSummaryCard, TaskForm, AuditFieldTimeline },
  props: {
    isEdit: Boolean,
    id: [String],
  },
  computed: {
    // When the page is reached from the map view (ViewMap passes ?from=map),
    // hide the Create Task / Export PDF header actions.
    openedFromMap() {
      return this.$route.query.from === "map";
    },
    itmCoords() {
      if (!this.finding || !this.finding.latitude || !this.finding.longitude) return null;
      return convertCoords(this.finding.latitude, this.finding.longitude, "WGS84", "2157");
    },
    selectedZoneId() {
      return this.newFinding?.zoneId || null;
    },
    submitStatusClass() {
      return this.submitStatus.startsWith("Success") ? "submit-success" : "submit-error";
    },
    finding() {
      // establish the dependency
      void this.findingVersion;
      return this._finding;
    },
    creationPhotoPreviews() {
      // Read the stable preview URL minted once by snapshotPhotoFile. Don't
      // call URL.createObjectURL() in a computed — runs on every render and
      // would leak blob URLs each tick.
      return (this.creationPhotos || [])
        .filter(p => p && p.previewUrl)
        .map(p => ({ url: p.previewUrl, name: p.name, size: p.size }));
    },
  },
  methods: {
    firstNonEmpty(...values) {
      for (const value of values) {
        if (typeof value === "string" && value.trim()) return value.trim();
      }
      return "";
    },
    getFormVisualMeta(form) {
      if (!form || typeof form !== "object") return { iconName: "", iconColour: "" };
      const legacyIconCode = this.firstNonEmpty(form.icon, form.Icon);
      const legacyIndex = iconsNums.findIndex((entry) => entry.icon === legacyIconCode);
      return {
        iconName: this.firstNonEmpty(form.iconName, form.IconName, icons[legacyIndex]?.icon),
        iconColour: this.firstNonEmpty(form.iconColour, form.IconColour),
      };
    },
    applyFormVisualMeta(finding, form) {
      if (!finding || typeof finding !== "object") return finding;
      const meta = this.getFormVisualMeta(form);
      const currentIconName = this.firstNonEmpty(finding.iconName, finding.IconName);
      const currentIconColour = this.firstNonEmpty(finding.iconColour, finding.IconColour);

      return {
        ...finding,
        IconName: currentIconName || meta.iconName || "",
        IconColour: currentIconColour || meta.iconColour || "",
      };
    },
    async loadLookups() {
      try {
        // Findings status/priority live in dedicated tables (Setups_Status_Finding,
        // Setups_Priority_Finding) and are served by the Dropdown API — NOT by
        // the generic /lookup/data/type system. Calling getLookupDataByType
        // returned empty silently, so dropdowns showed (0) items.
        const [statuses, priorities] = await Promise.all([
          fetchFindingStatusDropdown(),
          fetchFindingPriorityDropdown()
        ]);
        console.log("[loadLookups] raw FindingStatus dropdown:", statuses);
        console.log("[loadLookups] raw FindingPriority dropdown:", priorities);

        // Server returns DTOs as { FindingStatusId/PriorityId, FindingStatus/Priority, Colour }.
        // ASP.NET serializes to camelCase by default; tolerate either casing.
        this.findingStatuses = (statuses || []).map(item => {
          const id = item.findingStatusId ?? item.FindingStatusId;
          const name = item.findingStatus ?? item.FindingStatus;
          return {
            FindingStatusId: id, FindingStatus: name,
            findingStatusId: id, findingStatus: name,
            Colour: item.colour ?? item.Colour ?? ""
          };
        });
        this.priorities = (priorities || []).map(item => {
          const id = item.priorityId ?? item.PriorityId;
          const name = item.priority ?? item.Priority;
          return {
            PriorityId: id, Priority: name,
            priorityId: id, priority: name,
            Colour: item.colour ?? item.Colour ?? ""
          };
        });

        console.log("[loadLookups] mapped findingStatuses:", this.findingStatuses.length, this.findingStatuses);
        console.log("[loadLookups] mapped priorities:", this.priorities.length, this.priorities);

        // Default the create-flow selects to the first item so they aren't blank.
        // Edit mode loads its own values from the finding row, so don't overwrite.
        if (!this.isEdit && !this.id) {
          if (this.newFinding.statusId == null && this.findingStatuses.length) {
            this.newFinding.statusId = Number(this.findingStatuses[0].findingStatusId);
          }
          if (this.newFinding.priorityId == null && this.priorities.length) {
            this.newFinding.priorityId = Number(this.priorities[0].priorityId);
          }
        }
      } catch (e) {
        console.error("[loadLookups] FAILED — selects will be empty:", e);
        this.findingStatuses = [];
        this.priorities = [];
      }
    },
    async onPrintFinding() {
      try {

        if (!this.finding || !this.finding.findingId) {
          await this.dialog.confirm({
            title: "Error",
            message: "Finding ID missing!",
            variant: "error",
            buttons: "ok"
          });
          return;
        }

        // Route through apiClient (api/reports) so it hits the API host with the
        // bearer token. A relative fetch resolves against the SPA origin in prod,
        // which returns index.html instead of the PDF.
        const response = await generateFindingReport(this.finding.findingId);
        const contentType = response.headers["content-type"] || "";
        if (!contentType.includes("application/pdf")) {
          const text = typeof response.data?.text === "function" ? await response.data.text() : "";
          throw new Error(text || "Server returned non-PDF response");
        }

        const blob = response.data;

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Finding_${this.finding.findingId}.pdf`; // ✅ Trigger download
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

      } catch (err) {
        await this.dialog.confirm({
          title: "PDF Generation Failed",
          message: "Failed to generate PDF report.",
          variant: "error",
          buttons: "ok"
        });
      }
    },

    onTaskSaved(savedTask) {
      this.toast.success("Task created successfully");
      this.task = {};
      this.showCreateTaskDialog = false;
    },
    async fetchFinding() {
      this.loading = true;
      try {
        const resp = await getById(this.id);
        const baseFinding = JSON.parse(JSON.stringify(resp));
        let formRecord = null;

        const formId = baseFinding?.formId ?? baseFinding?.FormId ?? null;

        if (formId) {
          try {
            formRecord = await fetchForm(formId);
          } catch (e) {
            formRecord = null;
          }
        }

        const clean = this.applyFormVisualMeta(baseFinding, formRecord);
        // Stash the form so the summary card can fall back to its icon when
        // the finding row's IconName is NULL (older findings).
        this.formRecord = formRecord;
        const data = clean?.data;

        if (typeof data === "string" && data.trim()) {
          try {
            this.formJson = this.normalizeKeys(JSON.parse(data));
          } catch (e) {
            this.formJson = null;
          }
        } else if (formRecord) {
          try {
            const raw = formRecord.formStructure;
            this.formJson = typeof raw === "string" ? JSON.parse(raw) : raw;
          } catch (e) {
            this.formJson = null;
          }
        } else {
          this.formJson = null;
        }

        this._finding = markRaw(Object.freeze(clean));
        this.findingVersion++;

        // media fetch (unchanged)
        if (this.finding?.findingId) {
          this.media = await getPhotosByFindingId(this.finding.findingId);
        }
        if (clean?.findingId) {
          this.media = await getPhotosByFindingId(clean.findingId);
        } else {
          this.media = [];
        }
      } catch (err) {
      } finally {
        this.loading = false;
      }
    },
    async onMediaUpdated(updatedMedia) {
      // Update local media array when images are added/deleted
      this.media = updatedMedia || [];
    },
    async loadUsers() {
      this.users = await fetchUsers();
    },
    async loadFormJson(formId) {
      if (!formId) {
        this.formJson = null;
        return;
      }
      try {
        const record = await fetchForm(formId);
        const raw = record?.formStructure;
        if (raw == null || (typeof raw === "string" && !raw.trim())) {
          // Form template exists but has no structure persisted — surface it
          // instead of leaving the user staring at an indefinite spinner.
          console.warn("[loadFormJson] empty formStructure for formId", formId);
          this.formJson = null;
          this.toast?.error?.("This form has no structure defined. Pick another form template.");
          return;
        }
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        this.formJson = parsed;
      } catch (err) {
        console.error("[loadFormJson] failed for formId", formId, err);
        this.formJson = null;
        this.toast?.error?.("Could not load form template.");
      }
    },
    async loadCreationData() {
      try {
        const [zonesData, formsData, inspectionsData] = await Promise.all([
          fetchZones().catch(() => []),
          fetchFormsAll().catch(() => []),
          getAllInspections().catch(() => [])
        ]);
        this.zones = (zonesData || []).map(z => ({ name: z.name || z.Name, zoneId: z.zoneId || z.ZoneId }));
        this.formTemplates = (formsData || []).map(f => ({
          name: f.name || f.Name,
          formId: f.formId || f.FormId,
          iconName: this.getFormVisualMeta(f).iconName,
          iconColour: this.firstNonEmpty(f.iconColour, f.IconColour),
        }));
        this.inspections = (inspectionsData || []).map(i => ({ name: i.name || i.Name, inspectionId: i.inspectionId || i.InspectionId }));
      } catch (err) {
        console.error("Failed to load creation data:", err);
      }
    },
    onCreationStep1Next() {
      const valid = this.$refs.creationForm?.validate();
      if (!valid) return;
      
      // Load the selected form
      if (this.newFinding.formId) {
        this.loadFormJson(this.newFinding.formId);
      }
    },
    onCreationBack() {
      this.newFinding.zoneId = null;
      this.newFinding.formId = null;
      this.newFinding.inspectionId = null;
      this.formJson = null;
    },
    onLocationSelected(lat, lng) {
      this.newFinding.latitude = lat;
      this.newFinding.longitude = lng;
    },
    async handleCreationFormSubmit(formData) {
      // Re-entry guard: FormViewer's own 500ms cooldown protects only its inner
      // submit pipeline; if the API call here outlives that, a second click can
      // re-enter and create a duplicate finding (each call mints its own UUID).
      if (this.submittingLock) return;
      this.submittingLock = true;

      if (!this.newFinding.latitude || !this.newFinding.longitude) {
        this.toast?.error?.("Please select a location on the map");
        this.submittingLock = false;
        return;
      }

      try {
        // formData from FormViewer submit (includes audio URLs after upload)
        const filledFormData = Array.isArray(formData) ? formData : null;
        const selectedForm = (this.formTemplates || []).find(
          form => String(form.formId) === String(this.newFinding.formId)
        );

        const title = this.newFinding.title || "Finding from Desktop";
        const details = this.newFinding.details || title;

        const payload = {
          findingId: crypto.randomUUID(),
          zoneId: this.newFinding.zoneId,
          inspectionId: this.newFinding.inspectionId || null,
          formId: this.newFinding.formId || 0,
          title: title,
          details: details,
          isSafetyIssue: this.newFinding.isSafetyIssue,
          submitTime: findingDatePayload(this.newFinding.submitTime),
          dateMadeSafe: findingDatePayload(this.newFinding.dateMadeSafe),
          dateResolved: findingDatePayload(this.newFinding.dateResolved),
          statusId: this.newFinding.statusId || null,
          priorityId: this.newFinding.priorityId || null,
          latitude: this.newFinding.latitude,
          longitude: this.newFinding.longitude,
          data: filledFormData ? JSON.stringify(filledFormData) : "{}",
          iconName: selectedForm?.iconName || "",
          iconColour: selectedForm?.iconColour || "",
        };

        const newId = await createFinding(payload);

        // Upload any photos selected in Step 1. Has to happen after createFinding
        // because uploadPhoto requires the FindingId. Failures are non-fatal —
        // the finding is already saved, so we surface a toast and continue.
        const photos = Array.isArray(this.creationPhotos)
          ? this.creationPhotos.filter(p => p && p.blob instanceof Blob)
          : [];
        if (photos.length > 0) {
          const results = await Promise.allSettled(
            photos.map(p => uploadPhoto({
              Blob: p.blob,
              PhotoUrlId: crypto.randomUUID(),
              FindingId: newId,
              CreatedAt: new Date().toISOString(),
            }))
          );
          const failed = results.filter(r => r.status === "rejected").length;
          if (failed > 0) {
            this.toast?.error?.(`${failed} of ${photos.length} photo(s) failed to upload. You can retry from the edit screen.`);
          }
          // Release in-memory snapshots — bytes are on the server now (or the
          // user has been told to retry from edit).
          revokePhotoSnapshots(this.creationPhotos);
          this.creationPhotos = [];
        }

        if (filledFormData && filledFormData.length > 0) {
          const supportedTypes = ['text', 'textarea', 'number', 'email', 'phone', 'select', 'radio', 'radio-group', 'checkbox', 'checkbox-group', 'file', 'date', 'datetime', 'time', 'signature', 'photo', 'location', 'audio', 'rating', 'lookup', 'barcode'];
          const formClassArray = filledFormData
            .filter(field => {
              const fieldType = (field.type || field.Type || "").toLowerCase();
              return supportedTypes.includes(fieldType) && fieldType !== 'header' && fieldType !== 'divider';
            })
            .map(field => ({
              type: field.type || field.Type || "text",
              required: field.required || field.Required || false,
              label: field.label || field.Label || "",
              className: field.className || field.ClassName || "",
              name: field.name || field.Name || "",
              access: field.access !== false && field.Access !== false,
              value: field.value || field.Value || "",
              rows: field.rows || field.Rows || null,
              addPhoto: field.addPhoto || field.AddPhoto || false,
              values: field.values || field.Values || null,
            }));
          await submitFormResponse(newId, formClassArray);
        }

        this.toast?.success?.("Finding created successfully");
        this.$router.push({ name: "FindingList" });
      } catch (err) {
        console.error("Failed to create finding:", err);
        console.error("Error response:", err?.response);
        console.error("Error data:", err?.response?.data);
        const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to create finding";
        this.toast?.error?.(errorMsg);
      } finally {
        this.submittingLock = false;
      }
    },
    buildFilledStructure(formJson, answers) {
      // Similar to Inspection.vue - build form structure from answers
      if (!formJson || !Array.isArray(formJson)) return answers;
      
      // Filter out non-data field types
      const supportedTypes = ['text', 'textarea', 'number', 'email', 'phone', 'select', 'radio', 'radio-group', 'checkbox', 'checkbox-group', 'file', 'date', 'datetime', 'time', 'signature', 'photo', 'location', 'audio', 'rating', 'lookup', 'barcode'];
      
      const result = [];
      for (const field of formJson) {
        const fieldType = (field.type || field.Type || "").toLowerCase();
        // Skip header, divider, and other non-data field types
        if (fieldType === 'header' || fieldType === 'divider' || (!supportedTypes.includes(fieldType) && fieldType !== '')) {
          continue;
        }
        
        const key = field.name || field.fieldName || field.Name;
        if (key && answers[key] !== undefined) {
          result.push({
            type: field.type || field.Type || "text",
            required: field.required || field.Required || false,
            label: field.label || field.Label || "",
            className: field.className || field.ClassName || "",
            name: key,
            access: field.access !== false && field.Access !== false,
            value: answers[key],
            rows: field.rows || field.Rows || null,
            addPhoto: field.addPhoto || field.AddPhoto || false,
            values: field.values || field.Values || null,
          });
        } else if (key) {
          // Include data fields even if no answer (for structure)
          result.push({
            type: field.type || field.Type || "text",
            required: field.required || field.Required || false,
            label: field.label || field.Label || "",
            className: field.className || field.ClassName || "",
            name: key,
            access: field.access !== false && field.Access !== false,
            value: "",
            rows: field.rows || field.Rows || null,
            addPhoto: field.addPhoto || field.AddPhoto || false,
            values: field.values || field.Values || null,
          });
        }
      }
      return result;
    },
    async onSubmitTask() {
      try {
        // Defensive assignments with type coercion
        this.task.findingId = this.finding.findingId;
        this.task.zoneId = this.finding.zoneId || null;
        this.task.status = 1; //created
        this.task.assignedFrom = useAuthStore().user?.profile?.sub || null;

        this.task.latitude = this.finding.latitude;
        this.task.longitude = this.finding.longitude;
        this.task.accuracy = this.finding.accuracy;

        const result = await createTask(this.task);

        this.toast.success("Task created successfully");
        // Optionally reset form or navigate
      } catch (err) {
        this.toast.error("Failed to create task");
      }
    },
    async handleFormSubmit(formData) {
      if (this.submittingLock) return;
      this.submittingLock = true;

      this.submitting = true;
      this.submitStatus = "";

      try {
        await submitFormResponse(this.id, formData);
        this.submitStatus = "Success: Your response has been recorded.";
        this.toast?.success?.("Finding saved");
      } catch (err) {
        this.submitStatus = "Error submitting response. Please try again.";
        this.toast?.error?.("Failed to save");
      } finally {
        this.submitting = false;
        this.submittingLock = false;
      }
    },
    openLightbox(imgUrl) {
      this.lightbox.img = imgUrl;
      this.lightbox.show = true;
    },
    async triggerFormViewerSubmit() {
      debugger;
      await this.$nextTick();
      const fv = this.$refs.formViewer;
      if (!fv) return false;

      // If your FormViewer exposes a submit() method, use it:
      if (typeof fv.submit === "function") {
        fv.submit();
        return true;
      }

      // Fallback: click an internal submit button
      const btn =
        fv.$el?.querySelector?.('button[type="submit"]') ||
        fv.$el?.querySelector?.(".vf-submit, .submit");
      if (btn) {
        btn.click();
        return true;
      }
      return false;
    },

    async onSave() {
      // This will fire the same flow as clicking "Submit Response" in FormViewer,
      // which calls your existing handleFormSubmit(formData)
      const ok = this.triggerFormViewerSubmit();
      if (!ok) this.toast?.error?.("Couldn't submit the form");
    },
    // Single header Save: persist the Finding Overview field edits (if the panel
    // is in edit mode) AND the dynamic form answers, then refresh once. Both are
    // awaited so they don't race the reload.
    async onSaveAll() {
      if (this.savingAll) return;
      this.savingAll = true;
      try {
        let saved = false;

        // 1) Finding Overview fields (priority/status/inspection/date/etc.)
        const summary = this.$refs.summary;
        if (summary && summary.isEditing) {
          const dto = summary.buildDraftDto();
          if (dto) {
            await updateFinding(this._finding.findingId, dto, 2);
            summary.isEditing = false;
            summary.draft = null;
            saved = true;
          }
        }

        // 2) Dynamic form answers (prepared payload persisted directly).
        const fv = this.$refs.formViewer;
        if (fv && typeof fv.preparePayload === "function") {
          const payload = await fv.preparePayload();
          if (payload) {
            await submitFormResponse(this.id, payload);
            saved = true;
          }
        }

        if (saved) {
          await this.fetchFinding();
          this.toast?.success?.("Saved");
        } else {
          this.toast?.info?.("Nothing to save");
        }
      } catch (e) {
        console.error("Save all failed:", e);
        const errs = e?.response?.data?.errors;
        const errorMsg = e?.response?.data?.error || e?.response?.data?.message;
        const msg = errs ? Object.values(errs).flat().join(" · ") : (errorMsg || "Failed to save");
        this.toast?.error?.(msg);
      } finally {
        this.savingAll = false;
      }
    },
    async onSaveFinding(dto) {
      try {
        await updateFinding(this._finding.findingId, dto, 2); // the 2 here sends a smaller update dto. see controller for details.
        this.toast.success("Finding updated");
        await this.fetchFinding();
      } catch (e) {
        console.error("Failed to update finding:", e);
        console.error("Error response:", e?.response);
        console.error("Error data:", e?.response?.data);
        // show useful messages from server model validation
        const errs = e?.response?.data?.errors;
        const errorMsg = e?.response?.data?.error || e?.response?.data?.message;
        const msg = errs ? Object.values(errs).flat().join(" · ") : (errorMsg || "Failed to update finding");
        this.toast.error(msg);
      }
    },
    normalizeKeys(obj) {
      if (Array.isArray(obj)) {
        return obj.map(this.normalizeKeys);
      } else if (obj !== null && typeof obj === "object") {
        return Object.fromEntries(
          Object.entries(obj).map(([key, val]) => [
            key.charAt(0).toLowerCase() + key.slice(1),
            this.normalizeKeys(val),
          ])
        );
      }
      return obj;
    },

    getUserIconUrl(id) {
      // default to 1 if invalid, pad to two digits
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    onAddForm() {
      // this.$router.push({ name: "InspectionCreate" });
    },
    onAddInspection() {
      // this.$router.push({ name: "InspectionCreate" });
    },
    viewInspection(id) {
      // this.$router.push({ name: "InspectionView", params: { id } });
    },
    viewFinding(id) {
      //this.$router.push({ name: "Finding", params: {  id } });
    },
    editInspection(id) {
      // this.$router.push({ name: "InspectionEdit", params: { id } });
    },
    async onDeleteInspection(id) {
      const result = await this.dialog.confirm({
        title: "Delete Inspection",
        message: "Are you sure you want to delete this inspection?",
        variant: "warn",
        buttons: "ok-cancel"
      });
      if (result !== "ok") return;
      try {
        //await deleteItem(id);
        //this.fetchFinding();
      } catch (err) {
      }
    },
    formatDate(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleString();
    },
    formatFileSize(bytes) {
      if (!Number.isFinite(bytes)) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    removeCreationPhoto(idx) {
      const next = Array.isArray(this.creationPhotos) ? [...this.creationPhotos] : [];
      const [removed] = next.splice(idx, 1);
      revokePhotoSnapshot(removed);
      this.creationPhotos = next;
    },
    async onCreationPhotoInputChange(e) {
      // iOS-safe: snapshotPhotoFiles reads bytes into memory-backed Blobs
      // immediately so iOS can't evict the camera-temp file before submit.
      // Append (don't replace) so successive Add Photos clicks accumulate.
      const files = Array.from(e.target?.files || []);
      if (e.target) e.target.value = "";
      if (!files.length) return;

      const snaps = await snapshotPhotoFiles(files);
      if (snaps.length) {
        this.creationPhotos = [...(this.creationPhotos || []), ...snaps];
      }
      if (snaps.length < files.length) {
        this.toast?.error?.(`${files.length - snaps.length} photo(s) could not be read from the camera. Try again.`);
      }
    },
    getZoneCenter(geojson) {
      if (!geojson) return [53.41291, -8.24389]; // Center of Ireland
      try {
        const layer = L.geoJSON(geojson);
        const bounds = layer.getBounds();
        if (bounds.isValid()) {
          return [bounds.getCenter().lat, bounds.getCenter().lng];
        }
      } catch (err) {
      }
      return [53.41291, -8.24389]; // Center of Ireland
    },
  },
  async mounted() {
    this.loadUsers();
    this.loadLookups();

    if (this.isEdit && this.id) {
      // Edit mode - fetch existing finding
      await this.fetchFinding();
      // Load inspections so the editable Inspection dropdown in the
      // Finding Overview panel has options to pick from.
      try {
        const list = await getAllInspections();
        this.inspections = (list || []).map((i) => ({
          name: i.name || i.Name,
          inspectionId: i.inspectionId || i.InspectionId,
        }));
      } catch (e) {
        this.inspections = [];
      }
    } else if (!this.isEdit && !this.id) {
      // Creation mode - load zones and forms
      await this.loadCreationData();
    }
  },
  beforeUnmount() {
    revokePhotoSnapshots(this.creationPhotos);
  },
};
</script>
<style scoped>
.finding-shell-card,
.finding-panel-card {
  border-radius: 18px;
  border: 1px solid #e8edf0;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.finding-page-header {
  margin-bottom: 6px;
}

.finding-page-title {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.1;
  color: #223127;
}

.finding-page-subtitle {
  font-size: 0.98rem;
  color: #667085;
}

.finding-form-titlebar {
  background: linear-gradient(180deg, #ffffff 0%, #f8fbf9 100%);
}

.finding-form-shell {
  background: linear-gradient(180deg, #fcfdfd 0%, #f8fafc 100%);
}

.finding-form-shell :deep(.field-wrapper) {
  margin-bottom: 10px;
}

.card-map {
  flex: 1 1 0;
  position: relative;
  display: flex;
}
.card-map .map-container {
  width: 100% !important;
  height: 100% !important;
  border-radius: 0;
}
.submit-btn {
  visibility: hidden;
}

.sticky-card {
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.sticky-card::-webkit-scrollbar {
  width: 6px;
}

.sticky-card::-webkit-scrollbar-track {
  background: transparent;
}

.sticky-card::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.sticky-card::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}
</style>
